import React, { Suspense } from 'react';
import { Github, ExternalLink, Calendar, Star } from 'lucide-react';
import GlassCard from '../ui/GlassCard';
import GlowingText from '../ui/GlowingText';
import GradientButton from '../ui/GradientButton';
import LoadingSpinner from '../ui/LoadingSpinner';

const ProjectCard = React.lazy(() => import('../ui/ProjectCard'));

const ProjectsSection = ({ projects }) => {
  const featuredProjects = projects?.slice(0, 4) || [];

  return (
    <section id="projects" className="py-32 px-6 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-1/4 w-72 h-72 bg-gradient-to-br from-blue-500/10 to-emerald-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-gradient-to-tr from-violet-500/10 to-amber-500/10 rounded-full blur-3xl"></div>
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="h-full w-full" style={{
            backgroundImage: `
              linear-gradient(rgba(99, 102, 241, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(99, 102, 241, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}></div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <span className="text-caption text-violet-400 mb-4 block">Portfolio</span>
          <h2 className="text-display-lg mb-6">          <GlowingText 
              color="mixed"
              size="3xl"
              className="bg-gradient-to-r from-white via-blue-200 to-emerald-200 bg-clip-text text-transparent"
            >
              Featured Projects
            </GlowingText>
          </h2>
          <p className="text-body-lg text-neutral-300 max-w-2xl mx-auto">
            A collection of projects that showcase my expertise in full-stack development, 
            AI integration, and modern web technologies.
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {featuredProjects.map((project, index) => (
            <Suspense
              key={project.id}
              fallback={
                <div className="h-96 bg-surface-secondary/50 rounded-2xl animate-pulse flex items-center justify-center">
                  <LoadingSpinner />
                </div>
              }
            >
              <ProjectCard 
                project={project} 
                index={index}
                variant="enhanced"
              />
            </Suspense>
          ))}
        </div>

        {/* Enhanced Project Showcase */}
        <div className="mb-16">
          <h3 className="text-heading-lg text-center mb-12 text-neutral-200">
            Project Highlights
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: <Star className="w-6 h-6" />,
                title: "Innovation Focus",
                description: "Leveraging cutting-edge technologies like AI and cloud computing",
                color: "from-violet-500/20 to-purple-500/20"
              },
              {
                icon: <Github className="w-6 h-6" />,
                title: "Open Source",
                description: "Contributing to and maintaining open-source projects",
                color: "from-blue-500/20 to-cyan-500/20"
              },
              {
                icon: <ExternalLink className="w-6 h-6" />,
                title: "Production Ready",
                description: "All projects are deployment-ready with proper CI/CD",
                color: "from-emerald-500/20 to-teal-500/20"
              }
            ].map((highlight, index) => (
              <GlassCard
                key={index}
                variant="minimal"
                color="mixed"
                className="p-6 text-center group hover-lift"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className={`inline-flex p-3 rounded-xl bg-gradient-to-r ${highlight.color} mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <div className="text-white">
                    {highlight.icon}
                  </div>
                </div>
                <h4 className="text-heading-md text-white mb-2 group-hover:text-violet-300 transition-colors">
                  {highlight.title}
                </h4>
                <p className="text-sm text-neutral-400 group-hover:text-neutral-300 transition-colors">
                  {highlight.description}
                </p>
              </GlassCard>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <GradientButton
              size="lg"
              color="primary"
              glow={true}
              onClick={() => window.open("https://github.com/Seelam3653", "_blank")}
              icon={<Github />}
            >
              View All Projects
            </GradientButton>
            
            <GradientButton
              variant="outline"
              size="lg"
              color="accent"
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Start a Project
            </GradientButton>
          </div>
          
          <div className="mt-8 flex items-center justify-center gap-6 text-sm text-neutral-400">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>Updated regularly</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4" />
              <span>Quality focused</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection;
